import './index.css';
import '@stripe-internal/sail/sail.css';

import {createRoot} from 'react-dom/client';
import {AnalyticsReporter} from '@stripe-internal/analytics-js';
import {urlRedirect} from '@stripe-internal/safe-links';
import {MessageFormatElement} from 'intl-messageformat-parser';
import {App} from './App';
import {parseConfigFromPage} from './App/helpers/parseConfigFromPage';
import {isProduction} from './App/helpers/env';
import {isIframed} from './App/helpers/isIframed';
import {config as analyticsConfig} from './App/helpers/analytics';
import type {AnalyticEvents} from './App/helpers/analytics';

const lang =
  document.getElementsByTagName('html')[0].getAttribute('lang') || 'en';
const langMap: {[lang: string]: string} = {
  en: 'en-US',
  ja: 'ja-JP',
  'en-US-x-MESSAGE-ID': 'en-US-x-MESSAGE-ID',
  'en-US-x-STAR': 'en-US-x-STAR',
};

function regexSanitizeLocale(locale: string) {
  const regExp = /^[a-z]{2,3}-([A-Z]{2}|[0-9]{3})$/; // Must start with 2-3 lowercase letters and dash, and must end with 2 uppercase letters or 3 digits
  if (regExp.test(locale)) {
    return locale;
  }
}

const sanitize = (locale: string) => {
  return langMap[lang] || regexSanitizeLocale(locale) || 'en-US';
};

export type LocaleLoader = (
  locale: string,
) => Promise<Record<string, string> | Record<string, MessageFormatElement[]>>;

export function initializeApp(
  loadLocale: LocaleLoader,
  container: HTMLElement,
) {
  const config = parseConfigFromPage();
  if (!config) {
    throw new Error('Unable to parse config from page.');
  }

  const locale = sanitize(lang);

  const analytics = new AnalyticsReporter<AnalyticEvents>(analyticsConfig);

  analytics.action('support_conversations_iframe_loaded', {
    enableUniversalHelp: config.featureFlags.enableUniversalHelp,
    locale,
    audience: config.audience,
    source: config.entrypoint,
  });

  /**
   * When in production, the app should only be rendered in an iframe.
   * Redirect to the appropriate support site when attempting to render
   * the non-iframed version in production.
   */
  if (isProduction() && !isIframed()) {
    urlRedirect(config.urls.supportSite);
  } else {
    loadLocale(locale).then((messages) => {
      createRoot(container).render(<App locale={locale} messages={messages} />);
    });
  }
}
